import { Component, ErrorInfo, ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";
import i18n from "@/i18n/config";
import { reportError } from "@/lib/errorReporter";
import { isStaleAssetError, recoverFromStaleAssets } from "@/lib/pwaRecovery";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  recovering: boolean;
}

const getErrorBoundaryCopy = () => ({
  title: i18n.t("errorBoundary.title"),
  desc: i18n.t("errorBoundary.desc"),
  home: i18n.t("errorBoundary.home"),
  reload: i18n.t("errorBoundary.reload"),
});

class ErrorBoundary extends Component<Props, State> {
  public state: State = { hasError: false, error: null, recovering: false };

  public static getDerivedStateFromError(error: Error): State {
    const stale = isStaleAssetError(error);
    return { hasError: !stale, error, recovering: stale };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("[ErrorBoundary]", error, errorInfo);
    if (recoverFromStaleAssets(error)) {
      return;
    }
    reportError({
      message: error.message,
      stack: error.stack,
      error_type: error.name || "ReactError",
      source: "ErrorBoundary",
      severity: "fatal",
      metadata: { componentStack: errorInfo.componentStack?.slice(0, 2000) },
    });
  }


  private handleReload = () => {
    this.setState({ hasError: false, error: null, recovering: false });
    window.location.reload();
  };

  private handleHome = () => {
    this.setState({ hasError: false, error: null, recovering: false });
    window.location.href = "/";
  };

  public render() {
    if (this.state.recovering) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background">
          <div className="w-8 h-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
        </div>
      );
    }
    if (this.state.hasError) {
      const copy = getErrorBoundaryCopy();
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-6">
          <div className="max-w-md w-full text-center space-y-6">
            <div className="mx-auto w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
              <AlertTriangle className="w-8 h-8 text-destructive" />
            </div>
            <div className="space-y-2">
              <h1 className="text-2xl font-semibold text-foreground">
                {copy.title}
              </h1>
              <p className="text-muted-foreground">
                {copy.desc}
              </p>
            </div>
            <div className="flex gap-3 justify-center">
              <Button variant="outline" onClick={this.handleHome}>
                {copy.home}
              </Button>
              <Button onClick={this.handleReload}>{copy.reload}</Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
